import React, { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { wsManager } from '../api/websocket';

/**
 * Глобальный баннер «WS offline»: при обрыве соединения все live-данные
 * (цены, стаканы, спреды) на страницах ЗАМИРАЮТ без какой-либо индикации —
 * замершие данные выглядели живыми.
 *
 * Показываем не сразу и не в фоне (09.10 — «вечно вылезает, хотя всё
 * нормально»): обычное переподключение занимает 1–3 секунды, первое
 * подключение после загрузки страницы — до нескольких секунд, а вкладка в
 * фоне вообще не тот момент, когда человеку нужен красный баннер. Баннер
 * появляется, только если связи нет дольше GRACE_MS на видимой вкладке.
 */
const GRACE_MS = 6000;
export const WsOfflineBanner: React.FC = () => {
  const { t } = useTranslation('wsOfflineBanner');
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsub = wsManager.onConnectionChange((connected) => {
      if (connected) {
        if (timer) { clearTimeout(timer); timer = null; }
        setOffline(false);
      } else {
        if (!timer) {
          timer = setTimeout(() => {
            timer = null;
            // В фоне не показываем: проверим ещё раз, когда вкладку откроют.
            if (document.visibilityState === 'visible' && !wsManager.isConnected()) setOffline(true);
          }, GRACE_MS);
        }
      }
    });
    // Вернулись на вкладку, а связи всё ещё нет — отсчёт заново (менеджер в
    // этот момент уже переподключается, см. websocket.ts onVisible).
    const onVis = () => {
      if (document.visibilityState !== 'visible' || wsManager.isConnected() || timer) return;
      timer = setTimeout(() => {
        timer = null;
        if (!wsManager.isConnected()) setOffline(true);
      }, GRACE_MS);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
      unsub();
    };
  }, []);

  if (!offline) return null;
  return (
    <div className="fixed top-0 inset-x-0 z-[100] flex items-center justify-center gap-2 bg-trade-red/90 px-3 py-1.5 text-xs font-medium text-white shadow-lg">
      <WifiOff size={13} />
      {t('offline')}
    </div>
  );
};
